<template>
  <div class="auth-main v1">
    <div class="auth-wrapper">
      <div class="auth-form">
        <div class="card my-5">
          <div class="card-body">
            <div class="text-center">
              <h4 class="f-w-500 mb-1">{{ $t('auth.twoFactor.setupTitle') }}</h4>
              <p class="mb-3">{{ $t('auth.twoFactor.setupSubtitle') }}</p>
            </div>
            
            <!-- Alert messages -->
            <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
              {{ error }}
              <button type="button" class="btn-close" @click="error = null"></button>
            </div>
            
            <div v-if="success" class="alert alert-success alert-dismissible fade show" role="alert">
              {{ success }}
              <button type="button" class="btn-close" @click="success = null"></button>
            </div>
            
            <!-- Setup Steps -->
            <div v-if="!setupComplete">
              <!-- Step 1: QR Code -->
              <div class="mb-4">
                <h6 class="mb-2">{{ $t('auth.twoFactor.qrCodeTitle') }}</h6>
                <p class="text-muted small mb-3">{{ $t('auth.twoFactor.qrCodeSubtitle') }}</p>
                
                <div v-if="isLoading" class="text-center py-4">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                
                <div v-else-if="qrCodeUrl" class="text-center mb-3">
                  <img :src="qrCodeUrl" alt="2FA QR Code" class="img-fluid" style="max-width: 200px;" />
                </div>
              </div>
              
              <!-- Step 2: Secret Key -->
              <div class="mb-4">
                <h6 class="mb-2">{{ $t('auth.twoFactor.secretKeyTitle') }}</h6>
                <p class="text-muted small mb-3">{{ $t('auth.twoFactor.secretKeySubtitle') }}</p>
                
                <div v-if="secretKey" class="input-group mb-2">
                  <input 
                    type="text" 
                    class="form-control font-monospace" 
                    :value="secretKey" 
                    readonly 
                  />
                  <button 
                    class="btn btn-outline-secondary" 
                    type="button" 
                    @click="copySecretKey"
                    :disabled="isLoading"
                  >
                    <i class="fas fa-copy"></i> {{ $t('auth.twoFactor.copySecretKey') }}
                  </button>
                </div>
              </div>
              
              <!-- Step 3: Verification -->
              <div class="mb-4">
                <h6 class="mb-2">{{ $t('auth.twoFactor.verificationTitle') }}</h6>
                <p class="text-muted small mb-3">{{ $t('auth.twoFactor.verificationSubtitle') }}</p>
                
                <div class="mb-3">
                  <input
                    type="text"
                    class="form-control text-center"
                    :placeholder="$t('auth.twoFactor.verificationCodePlaceholder')"
                    v-model="verificationCode"
                    maxlength="6"
                    pattern="[0-9]{6}"
                    required
                  />
                  <div v-if="v$.verificationCode.$error" class="text-danger text-start">
                    {{ v$.verificationCode.$errors[0].$message }}
                  </div>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="d-grid gap-2">
                <button 
                  type="button" 
                  class="btn btn-primary" 
                  @click="enable2FA" 
                  :disabled="isLoading || !verificationCode"
                >
                  <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                  {{ $t('auth.twoFactor.completeSetup') }}
                </button>
                
                <router-link to="/login" class="btn btn-outline-secondary">
                  {{ $t('auth.twoFactor.backToLogin') }}
                </router-link>
              </div>
            </div>
            
            <!-- Setup Complete -->
            <div v-else class="text-center">
              <div class="mb-4">
                <i class="fas fa-check-circle text-success" style="font-size: 3rem;"></i>
              </div>
              <h5 class="text-success mb-3">{{ $t('auth.twoFactor.setupSuccess') }}</h5>
              <div class="d-grid">
                <router-link to="/login" class="btn btn-primary">
                  {{ $t('auth.twoFactor.backToLogin') }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, minLength, maxLength } from '@vuelidate/validators';
import authService from '@/services/authService';

const router = useRouter();
const { t } = useI18n();

const qrCodeUrl = ref('');
const secretKey = ref('');
const verificationCode = ref('');
const error = ref(null);
const success = ref(null);
const isLoading = ref(false);
const setupComplete = ref(false);

// Validation rules
const rules = {
  verificationCode: { 
    required, 
    minLength: minLength(6),
    maxLength: maxLength(6)
  }
};

const v$ = useVuelidate(rules, { verificationCode });

onMounted(async () => {
  await loadSetupData();
});

const loadSetupData = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const setupData = await authService.setup2FA();
    
    
    // Use the correct field names from API response
    qrCodeUrl.value = setupData.qrCodeBase64 ? `data:image/png;base64,${setupData.qrCodeBase64}` : '';
    secretKey.value = setupData.secretKey || '';
  } catch (err) {
    console.error('2FA setup error:', err);
    error.value = err.response?.data?.error?.message || 
                  err.response?.data?.message || 
                  t('auth.twoFactor.setupError');
  } finally {
    isLoading.value = false;
  }
};

const enable2FA = async () => {
  error.value = null;
  success.value = null;
  
  // Validate form
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;
  
  isLoading.value = true;
  
  try {
    await authService.enable2FA(verificationCode.value);
    setupComplete.value = true;
    success.value = t('auth.twoFactor.setupSuccess');
  } catch (err) {
    console.error('Enable 2FA error:', err);
    error.value = err.response?.data?.error?.message || 
                  err.response?.data?.message || 
                  t('auth.twoFactor.setupError');
  } finally {
    isLoading.value = false;
  }
};

const copySecretKey = async () => {
  try {
    await navigator.clipboard.writeText(secretKey.value);
    success.value = t('auth.twoFactor.secretKeyCopied');
    setTimeout(() => {
      success.value = null;
    }, 3000);
  } catch (err) {
    console.error('Copy error:', err);
    error.value = 'Failed to copy secret key';
  }
};
</script>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9rem;
  letter-spacing: 0.1em;
}

.auth-main {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.auth-wrapper {
  width: 100%;
  max-width: 500px;
  padding: 0 15px;
}

.card {
  border: none;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.btn {
  border-radius: 8px;
  padding: 12px 20px;
  font-weight: 500;
}

.form-control {
  border-radius: 8px;
  border: 1px solid #e1e5e9;
  padding: 12px 15px;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.text-center img {
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 10px;
}
</style> 